<template>
  <panel
    ref="root"
    class="h-full min-h-screen md:min-h-auto project relative"
    @close="closePanel"
  >
    <template slot="header">
      <div>
        <small>#</small
        ><span class="inline-block pr-4 select-all text-xl" v-text="id" />
        <strong
          v-if="project"
          class="inline-block select-all text-xl"
          v-text="project.filename"
          :class="{ 'text-gray-500 italic': !project.hasFilename }"
        />
      </div>
      <quick-actions v-if="project" :project="project" :i18n="i18n" />
      <slide-y-down-transition
        v-if="project"
        group
        class="flex flex-wrap justify-between -mb-1 mt-3 -mx-2 text-gray-800 text-sm border-b border-gray-700"
      >
        <router-link
          v-for="route in routes"
          :to="{ name: route.name, params: { id } }"
          :key="route.name"
          class="bg-gray-300 pl-3 pr-1 py-1 mx-1 rounded-t border-b border-gray-700 -mb-px flex-auto"
          :class="{
            'bg-action text-black':
              route.highlight && $route.name != route.name,
            'hover:bg-red-600 hover:text-red-100': route.destructive,
            'text-red-800': route.destructive && $route.name == route.name,
            'hover:bg-menu-hover': !route.destructive,
          }"
          exact-active-class="border-full-white bg-full-white text-black"
          exact-path
        >
          <font-awesome-icon :icon="route.icon" v-if="route.icon" fixed-width />
          {{ route.text }}
        </router-link>
      </slide-y-down-transition>
    </template>

    <template slot="main">
      <transition name="fade" mode="out-in">
        <router-view
          v-if="project"
          :project="project"
          :i18n="i18n"
          @reload="reload"
          :key="project.id"
        />
        <div
          v-else-if="error"
          class="p-4 border border-red-200 bg-red-100 text-red-800"
        >
          {{ errorMessage || error }}
        </div>
        <div v-else class="pt-8 self-center">
          <loading text-color="text-gray-700" size="text-3xl" />
        </div>
      </transition>
    </template>
    <template slot="footer">
      <div class="flex justify-between pr-1">
        <fade-transition>
          <button
            v-if="$route.name == 'project.index'"
            @click="reload"
            class="text-sm m-1 button button-base action-secondary"
          >
            <font-awesome-icon
              v-if="isLoading"
              icon="spinner"
              spin
              fixed-width
            />
            <font-awesome-icon
              v-else
              icon="redo-alt"
              class="text-gray-700"
              fixed-width
            />
            <span class="pl-3 text-gray-700" v-text="i18n.refresh" />
          </button>
        </fade-transition>
        <div />
        <ranch-button
          :label="i18n.buttons.support"
          icon="envelope-open-text"
          action="action-secondary"
          :route="supportLocation"
        />
      </div>
    </template>
  </panel>
</template>

<script>
import useSWRV from "@/store/swrv";
import { useStore, computed } from "@/store/composition";

import Panel from "../Design/Panel";
import _QuickActions from "../Project/_QuickActions.vue";
import { ref, watch, nextTick } from "@vue/composition-api";

export default {
  props: ["id"],
  components: { Panel, "quick-actions": _QuickActions },
  methods: {
    closePanel() {
      this.$router.push({
        name: "welcome",
        params: {
          type: this.$route.params.type,
          locale: this.$route.params.locale,
        },
      });
    },
  },
  computed: {
    supportLocation() {
      const application = this.project && this.project.application;
      return {
        query: Object.assign({}, this.$route.query, {
          supportModal: "project",
          usual_software: application && application.slug,
          software_version:
            application && application.release && application.release.version,
          project_ids: this.id,
        }),
      };
    },
    routes() {
      const prefix =
        this.$route.name.substr(0, 5) === "queue" ? "queue" : "project";
      const routes = [
        {
          name: prefix + ".index",
          text: this.i18n.tab.index.label,
        },
      ];

      if (this.showPriorityTab) {
        routes.push({
          name: prefix + ".priority",
          text: this.i18n.tab.priority.label,
          highlight: this.showPriorityTab > 1,
          icon: "layer-group",
        });
      }
      if (this.showDownloadTab) {
        routes.push({
          name: prefix + ".download",
          text: this.i18n.tab.download.label,
          highlight: this.showDownloadTab > 1,
          icon: "download",
        });
      }
      if (this.showUploadTab) {
        routes.push({
          name: prefix + ".upload",
          text: this.i18n.tab.upload.label,
          highlight: this.showUploadTab > 1,
          icon: "upload",
        });
      }
      if (this.showLogsTab) {
        routes.push({
          name: prefix + ".logs",
          text: this.i18n.tab.logs.label,
          highlight: this.showLogsTab > 1,
          icon: this.showLogsTab > 1 ? ["fas", "clipboard-list"] : null,
        });
      }

      if (this.project.status === "created") {
        routes.push({
          name: prefix + ".discard",
          text: this.i18n.tab.discard.label,
          destructive: true,
        });
      } else if (this.project.status === "uploading") {
        routes.push({
          name: prefix + ".discard",
          text: this.i18n.tab["discard"].label,
          destructive: true,
        });
      } else if (
        (this.project.status === "inwaiting" ||
          this.project.status === "uploaded") &&
        this.project.status_error.id !== "removed_by_user"
      ) {
        routes.push({
          name: prefix + ".remove",
          text: this.i18n.tab.remove.label,
          destructive: true,
        });
      } else if (
        this.project.status === "inprogress" &&
        this.project.status_error.id !== "stopped_by_user"
      ) {
        routes.push({
          name: prefix + ".stop",
          text: this.i18n.tab.stop.label,
          destructive: true,
        });
      }

      return routes;
    },
    showPriorityTab() {
      // 2 means highlight
      if (
        this.project.status === "created" &&
        this.project.application.renderer !== null
      ) {
        return this.project.priority.id === null ? 2 : 1;
      }
      if (this.project.status === "inwaiting") {
        return 2;
      }
      return 0;
    },
    showDownloadTab() {
      if (this.project.hasFramesToDownload) {
        return 2;
      }
      return this.project.status === "finished";
    },
    showUploadTab() {
      // 2 means highlight
      if (
        this.project.status === "uploading" ||
        (this.project.status === "created" && this.project.priority.id !== null)
      ) {
        return 2;
      }
      return this.uploadAttempts.length > 0;
    },

    showLogsTab() {
      // 2 means highlight
      if (this.project.status === "invalid") {
        return 2;
      }
      return !["created", "uploading"].includes(this.project.status);
    },

    uploadAttempts() {
      return this.$store.getters["tuspload/attempts"](this.project.id);
    },
  },
  setup(props) {
    const { data, error, isLoading, reload, isLoaded } = useSWRV(
      () => "/api/app/projects/" + props.id
    );
    const project = computed(
      () =>
        data.value &&
        data.value.data &&
        data.value.data.id == props.id &&
        data.value.data
    );

    const store = useStore();
    const i18n = computed(
      () => store.state.dashboard.i18n && store.state.dashboard.i18n.project
    );

    const root = ref(null);
    let previousId = null;
    watch(project, () => {
      if (!project.value || previousId == project.value.id) {
        return;
      }
      nextTick(() => {
        root.value.$el.scrollIntoView();
      });
    });

    return {
      root,

      project,

      isLoading,
      isLoaded,
      reload,
      error,
      errorMessage: computed(
        () =>
          error.value &&
          error.value.response &&
          error.value.response.data &&
          error.value.response.data.message
      ),

      i18n,
    };
  },
};
</script>
